/**
 * URS-F486 StatusesPanel
 *
 * Panel generico para configurar estados (label ES/EN, color, orden,
 * default). Los estados del sistema (`is_system=true`) solo se pueden
 * renombrar/colorear/reordenar/marcar como default. Los custom pueden
 * crearse, editarse y eliminarse.
 *
 * Se instancia dos veces desde Settings.js:
 *  - Estados de Estudio       -> /api/config/study-statuses
 *  - Estados de Cita          -> /api/config/appointment-statuses
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Plus, Trash, PencilSimple, CheckCircle, Lock } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = () => ({
  status_id: null,
  key: '',
  label_es: '',
  label_en: '',
  color: '#5b6473',
  order: 100,
  is_default: false,
  is_system: false,
  counts_as_occupied: true,
});

export default function StatusesPanel({ lang = 'es', endpoint, entity }) {
  // entity: 'study' | 'appointment'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/config/${endpoint}`, { withCredentials: true });
      setItems(r.data || []);
    } catch (err) {
      toast.error(errText(err));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [endpoint]);

  const openNew = () => setEditing(emptyForm());
  const openEdit = (item) => setEditing({ ...item });

  const save = async () => {
    if (!editing) return;
    const payload = {
      label_es: (editing.label_es || '').trim(),
      label_en: (editing.label_en || '').trim(),
      color: editing.color || '#5b6473',
      order: Number(editing.order) || 100,
      is_default: !!editing.is_default,
    };
    if (entity === 'appointment') {
      payload.counts_as_occupied = editing.counts_as_occupied !== false;
    }
    if (!payload.label_es || !payload.label_en) {
      toast.error(lang === 'es' ? 'Etiqueta ES y EN son obligatorias' : 'ES and EN labels are required');
      return;
    }
    try {
      if (editing.status_id) {
        // Update: para custom permitimos cambiar la key
        if (!editing.is_system && editing.key) payload.key = editing.key;
        await axios.put(`${API_URL}/api/config/${endpoint}/${editing.status_id}`, payload, { withCredentials: true });
        toast.success(lang === 'es' ? 'Estado actualizado' : 'Status updated');
      } else {
        // Create custom: key opcional (autogenerada del label)
        if (editing.key) payload.key = editing.key;
        await axios.post(`${API_URL}/api/config/${endpoint}`, payload, { withCredentials: true });
        toast.success(lang === 'es' ? 'Estado creado' : 'Status created');
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API_URL}/api/config/${endpoint}/${confirmDelete.status_id}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Estado eliminado' : 'Status deleted');
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const title = entity === 'study'
    ? (lang === 'es' ? 'Estados del Estudio' : 'Study statuses')
    : (lang === 'es' ? 'Estados de la Cita' : 'Appointment statuses');
  const description = entity === 'study'
    ? (lang === 'es'
        ? 'Configura los estados que puede tener un estudio (por ejemplo, Activo, Finalizado, Cancelado). Los estados del sistema pueden renombrarse y colorearse pero no eliminarse — hay lógica de negocio anclada a su clave interna. Puedes crear estados personalizados y elegir cuál es el estado por defecto para los nuevos estudios.'
        : 'Configure the statuses a study can have. System statuses can be renamed and recolored but not deleted. You can add custom ones and pick which one is the default for new studies.')
    : (lang === 'es'
        ? 'Configura los estados que puede tener una cita en la agenda (Citada, Sala de espera, Especialista, etc.). Los estados del sistema pueden renombrarse y colorearse pero no eliminarse — hay lógica de negocio anclada a su clave interna. Puedes crear estados personalizados y elegir cuál es el estado por defecto para las nuevas citas.'
        : 'Configure the statuses an appointment can have. System statuses can be renamed and recolored but not deleted. You can add custom ones and pick which one is the default for new appointments.');

  return (
    <div className="space-y-4" data-testid={`statuses-panel-${entity}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium text-stone-900">{title}</h3>
          <p className="text-xs text-stone-500 mt-1 max-w-2xl">{description}</p>
        </div>
        <Button
          onClick={openNew}
          data-testid={`add-status-btn-${entity}`}
          className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white gap-2 rounded-sm"
        >
          <Plus size={14} />{lang === 'es' ? 'Nuevo estado' : 'New status'}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-stone-400 text-center py-6">{lang === 'es' ? 'Cargando…' : 'Loading…'}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-6 border border-dashed border-stone-200 rounded-sm">
          {lang === 'es' ? 'No hay estados configurados.' : 'No statuses configured.'}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">{lang === 'es' ? 'Orden' : 'Order'}</TableHead>
              <TableHead className="w-16">{lang === 'es' ? 'Color' : 'Color'}</TableHead>
              <TableHead>{lang === 'es' ? 'Etiqueta ES' : 'ES label'}</TableHead>
              <TableHead>{lang === 'es' ? 'Etiqueta EN' : 'EN label'}</TableHead>
              <TableHead className="w-40 font-mono text-xs">{lang === 'es' ? 'Clave interna' : 'Internal key'}</TableHead>
              <TableHead className="w-28 text-center">{lang === 'es' ? 'Por defecto' : 'Default'}</TableHead>
              <TableHead className="w-24 text-center">{lang === 'es' ? 'Tipo' : 'Type'}</TableHead>
              <TableHead className="w-24">{lang === 'es' ? 'Acciones' : 'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(it => (
              <TableRow key={it.status_id} data-testid={`status-row-${it.key}`}>
                <TableCell className="text-xs text-stone-500">{it.order}</TableCell>
                <TableCell>
                  <span
                    className="inline-block w-6 h-6 rounded-sm border border-stone-200"
                    style={{ backgroundColor: it.color }}
                    title={it.color}
                    data-testid={`status-color-swatch-${it.key}`}
                  />
                </TableCell>
                <TableCell className="text-sm text-stone-800">{it.label_es}</TableCell>
                <TableCell className="text-sm text-stone-600">{it.label_en}</TableCell>
                <TableCell className="font-mono text-xs text-stone-500">{it.key}</TableCell>
                <TableCell className="text-center">
                  {it.is_default ? (
                    <Badge variant="outline" className="gap-1 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid={`status-default-badge-${it.key}`}>
                      <CheckCircle size={10} weight="fill" />{lang === 'es' ? 'Default' : 'Default'}
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell className="text-center">
                  {it.is_system ? (
                    <Badge variant="outline" className="gap-1 bg-stone-50 text-stone-500 border-stone-200 text-[10px]">
                      <Lock size={10} />{lang === 'es' ? 'Sistema' : 'System'}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]">
                      {lang === 'es' ? 'Custom' : 'Custom'}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-stone-400 hover:text-[#1f3e80]"
                      onClick={() => openEdit(it)}
                      data-testid={`edit-status-${it.key}`}
                    >
                      <PencilSimple size={14} />
                    </Button>
                    {!it.is_system && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-stone-400 hover:text-red-500"
                        onClick={() => setConfirmDelete(it)}
                        data-testid={`delete-status-${it.key}`}
                      >
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Editor modal */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="status-editor">
          <DialogHeader>
            <DialogTitle>
              {editing?.status_id
                ? (lang === 'es' ? 'Editar estado' : 'Edit status')
                : (lang === 'es' ? 'Nuevo estado' : 'New status')}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {editing?.is_system
                ? (lang === 'es'
                    ? 'Este es un estado del sistema. Puedes cambiar la etiqueta visible, el color, el orden y marcarlo como default. La clave interna no puede modificarse porque está anclada a lógica de negocio.'
                    : 'This is a system status. You can rename it, recolor, reorder or mark as default. The internal key is fixed.')
                : (lang === 'es'
                    ? 'Configura un estado personalizado. La clave se genera automáticamente a partir de la etiqueta ES si la dejas vacía.'
                    : 'Configure a custom status. The key is auto-generated from the ES label if left empty.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1">{lang === 'es' ? 'Etiqueta ES' : 'ES label'} *</Label>
                <Input
                  data-testid="status-label-es-input"
                  value={editing?.label_es || ''}
                  onChange={e => setEditing(p => ({ ...p, label_es: e.target.value }))}
                  maxLength={60}
                />
              </div>
              <div>
                <Label className="text-xs mb-1">{lang === 'es' ? 'Etiqueta EN' : 'EN label'} *</Label>
                <Input
                  data-testid="status-label-en-input"
                  value={editing?.label_en || ''}
                  onChange={e => setEditing(p => ({ ...p, label_en: e.target.value }))}
                  maxLength={60}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs mb-1">{lang === 'es' ? 'Color' : 'Color'}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editing?.color || '#5b6473'}
                    onChange={e => setEditing(p => ({ ...p, color: e.target.value }))}
                    className="h-9 w-12 border border-stone-200 rounded-sm cursor-pointer"
                    data-testid="status-color-input"
                  />
                  <Input
                    className="font-mono text-xs h-9"
                    value={editing?.color || ''}
                    onChange={e => setEditing(p => ({ ...p, color: e.target.value }))}
                    maxLength={9}
                    data-testid="status-color-hex-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1">{lang === 'es' ? 'Orden' : 'Order'}</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={editing?.order ?? 100}
                  onChange={e => setEditing(p => ({ ...p, order: e.target.value }))}
                  data-testid="status-order-input"
                />
              </div>
              <div>
                <Label className="text-xs mb-1">{lang === 'es' ? 'Clave interna' : 'Internal key'}</Label>
                <Input
                  disabled={!!editing?.is_system}
                  className="font-mono text-xs bg-stone-50 disabled:opacity-70"
                  value={editing?.key || ''}
                  onChange={e => setEditing(p => ({ ...p, key: e.target.value }))}
                  placeholder={editing?.status_id ? '' : (lang === 'es' ? 'auto' : 'auto')}
                  data-testid="status-key-input"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-700 pt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!editing?.is_default}
                onChange={e => setEditing(p => ({ ...p, is_default: e.target.checked }))}
                data-testid="status-is-default-checkbox"
                className="h-4 w-4 accent-emerald-600"
              />
              <span>
                {entity === 'study'
                  ? (lang === 'es' ? 'Usar como estado por defecto al crear un estudio nuevo' : 'Use as default status for new studies')
                  : (lang === 'es' ? 'Usar como estado por defecto al crear una cita nueva' : 'Use as default status for new appointments')}
              </span>
            </label>
            {entity === 'appointment' && (
              <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer border-t border-stone-100 pt-3">
                <input
                  type="checkbox"
                  checked={editing?.counts_as_occupied !== false}
                  onChange={e => setEditing(p => ({ ...p, counts_as_occupied: e.target.checked }))}
                  data-testid="status-counts-as-occupied-checkbox"
                  className="h-4 w-4 mt-0.5 accent-emerald-600"
                />
                <span className="flex flex-col leading-tight">
                  <span>{lang === 'es' ? 'Este estado ocupa el hueco' : 'This status occupies the slot'}</span>
                  <span className="text-[11px] text-stone-500">
                    {lang === 'es'
                      ? 'Si está marcado, las citas con este estado se cuentan como "ocupado" en Eficiencia de Agenda. Desmárcalo solo para estados que liberan el hueco (por ejemplo Anulada).'
                      : 'When checked, appointments with this status count as "occupied" in Agenda Efficiency. Uncheck only for statuses that free the slot.'}
                  </span>
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={save}
              data-testid="save-status-btn"
              className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white"
            >
              {lang === 'es' ? 'Guardar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{lang === 'es' ? 'Eliminar estado' : 'Delete status'}</DialogTitle>
            <DialogDescription>
              {lang === 'es'
                ? `¿Eliminar el estado "${confirmDelete?.label_es}"? Los registros existentes que ya usen esta clave seguirán mostrándola en crudo hasta que se actualicen manualmente.`
                : `Delete status "${confirmDelete?.label_en}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={doDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-status-btn"
            >
              {lang === 'es' ? 'Eliminar' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
